import { Box } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { Fragment, MouseEventHandler, useContext, useState } from 'react';
import { ProductionOrganizationInfo } from 'sr-types/lib/production/v1/graphql';
import { LabelValueDisplay } from '../../attributes/AttributeValue';
import { SECURITY_ACTIONS, useSecurity } from '../../common/auth/Security';
import { UserContext } from '../../common/auth/UserContext';
import { VerticalSeparator } from '../../common/components/DelimitedLabels';
import { GROUP_BY_FIELDS } from '../../common/components/forms/groupByConfigurations/groupByConstants';
import { PUNCTUATION_MARKS_CONSTANTS } from '../../common/constants/constants';
import { ENTITY_CONSTANTS } from '../../common/constants/entityConstants';
import { useI18n } from '../../common/i18n/I18n';
import ResponsiveLayout from '../../common/ResponsiveLayout';
import { useStyling } from '../../common/Theme';
import useHistory from '../../common/utils/useHistory';
import { getWorkspaceDataCardPreference } from '../DataList/ProductionDataListConfiguration/productionDataListConfigurationUtils';
import AddMore from './AddMore';
import NoContent from './NoContent';
import NoValueAvailable from './NoValueAvailable';
import { groupItemsByRole } from './productionHeaderUtils';

type AssociateOrgsProps = {
    productionOrgs: ProductionOrganizationInfo[];
    onAdd?: MouseEventHandler<HTMLButtonElement>;
    isSaving?: boolean;
    isReadOnly?: boolean;
};

const comma = `${PUNCTUATION_MARKS_CONSTANTS.comma} `;

export default function AssociatedOrgsTitle({
    productionOrgs,
    onAdd,
    isSaving = false,
    isReadOnly = false
}: AssociateOrgsProps) {
    const { application, preferences } = useContext(UserContext);
    const { changeRoute } = useHistory();
    const { theme } = useStyling();
    const [lastVisibleIndex, setLastVisibleIndex] = useState<number | undefined>();

    const noValueText = useI18n('production.header.keyOrgs.notAvailable');
    const noRoleAssignedText = useI18n('production.header.keyPeople.no.role.assigned');

    const { workspacePreferenceValue } = getWorkspaceDataCardPreference(preferences);

    const isGroupByNamePreference =
        workspacePreferenceValue.keyOrganizationsGroupBy === GROUP_BY_FIELDS.keyOrganizationName;

    const { allowed: canEdit } = useSecurity(SECURITY_ACTIONS.EDIT, {
        entityName: ENTITY_CONSTANTS.production
    });
    const canAddKeyOrgs = !isReadOnly && canEdit;

    const openOrganization = (event: React.MouseEvent<HTMLElement>, orgId?: string) => {
        event.stopPropagation();
        if (orgId) changeRoute(`${application.home}/organizations/${orgId}/details`);
    };

    const entries = isGroupByNamePreference
        ? (productionOrgs || []).map((prodOrg) => ({
              id: prodOrg.organizationReference?.id,
              label: !isEmpty(prodOrg.productionRoles) ? prodOrg.productionRoles.join(comma) : noRoleAssignedText,
              value: prodOrg.name,
              orgId: prodOrg.organizationReference?.id
          }))
        : groupItemsByRole(productionOrgs || [], (org) => org.productionRoles, noRoleAssignedText).map(
              ({ role, items }) => ({
                  id: role,
                  label: (
                      <>
                          {items.map(({ name, organizationReference }, index) => (
                              <Fragment key={organizationReference?.id ?? index}>
                                  {index > 0 && comma}
                                  <Box
                                      component="span"
                                      className="clickable"
                                      onClick={(event) => openOrganization(event, organizationReference?.id)}
                                  >
                                      {name}
                                  </Box>
                              </Fragment>
                          ))}
                      </>
                  ),
                  value: role,
                  orgId: items.length === 1 ? items[0].organizationReference?.id : undefined
              })
          );

    return entries.length ? (
        <ResponsiveLayout
            gap={1.5}
            sx={{ alignItems: 'center' }}
            tileWidth={110}
            setLastElementToFitIndex={setLastVisibleIndex}
            fixedEndElement={
                canAddKeyOrgs ? (
                    <AddMore i18nToken="production.header.keyOrgs.addKeyOrgs" onAdd={onAdd} disabled={isSaving} />
                ) : undefined
            }
        >
            {entries.map(({ id, label, value, orgId }, index) => {
                const isInOverflow = lastVisibleIndex !== undefined && index > lastVisibleIndex;
                const showSeparator = index < entries.length - 1 && !isInOverflow;

                return (
                    <Fragment key={id ?? index}>
                        <LabelValueDisplay
                            label={label}
                            value={value}
                            labelOnTop={false}
                            onClickValue={(event) => openOrganization(event, orgId)}
                        />
                        {showSeparator && <VerticalSeparator />}
                    </Fragment>
                );
            })}
        </ResponsiveLayout>
    ) : isReadOnly ? (
        <NoValueAvailable text={noValueText} />
    ) : canAddKeyOrgs ? (
        <NoContent
            i18nToken="production.header.keyOrgs.addKeyOrgs"
            onAdd={onAdd}
            disabled={isSaving}
            containerSx={{
                g: 1,
                justifyContent: 'center',
                width: '220px',
                height: '43px',
                color: theme.palette.primary.main,
                '&:hover': {
                    cursor: 'pointer'
                }
            }}
        />
    ) : null;
}
